import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {RootState} from '../../../../../app/store';
import {
    BotType,
    ClickAction,
    FullBotUi,
    ConvoStarterVisibility,
    LocaleTag,
    UpdateVehicleDetailsButtonRequest,
    UpdateWidgetConvoStartersRequest,
    WidgetPosition
} from '../../../../../api/generated';
import {botApi, RequestStatus, widgetAppearanceApi} from '../../../../../api/apiConfiguration';
import {DEFAULT_SURVEY_MESSAGE_COUNT, normalizeSurveyMessageCount} from '../../surveySettings';

const DEFAULT_EDITOR_LOCALE = LocaleTag.EnGb;

export const DEFAULT_ASSISTANT_NAME = 'AIME';

export const MAX_FAB_BUTTONS = 6;

export const DEFAULT_TERMS_URL = 'https://motortech.ai/hubfs/New%20Docs%20Keyloop%202026/Motortech%20AI%20-%20Privacy%20Policy%20April%202026.pdf';

const DEFAULT_VARIANT_TEMPLATE: Omit<WidgetAppearanceLocaleVariantUI, 'locale'> = {
    id: undefined,
    brandColour: '#377283',
    widgetPosition: WidgetPosition.Right,
    horizontalOffsetPx: 20,
    verticalOffsetPx: 20,
    hidePoweredBy: false,
    showFooterMessage: false,
    footerMessage: 'Replace this with your message',
    showTerms: false,
    termsTitle: 'Terms & Conditions',
    termsUrl: DEFAULT_TERMS_URL,
    promptPopUpView: true,
    popUpDelaySec: 5,
    initialBotMessage: "Hi, I'm AIME, your AI assistant.\nHow can I help you {time}?",
    convoStarters: [],
    vehicleDetailsButtons: [],
    enablePersonalizedVehicleDescription: false,
    gtmAnalyticsEnabled: false,
    assistantName: DEFAULT_ASSISTANT_NAME,
    personaPrompt: ''
};

// Survey settings are stored on the assistant, but are edited and saved together with the widget appearance.
export interface FeedbackSettingsUI {
    surveyAfterMessagesEnabled: boolean;
    surveyAfterMessagesCount?: number;
    surveyAfterBookingEnabled: boolean;
}

export type FeedbackSettingsPartial = Partial<FeedbackSettingsUI>;

const DEFAULT_FEEDBACK_SETTINGS: FeedbackSettingsUI = {
    surveyAfterMessagesEnabled: false,
    surveyAfterMessagesCount: DEFAULT_SURVEY_MESSAGE_COUNT,
    surveyAfterBookingEnabled: false
};

export const feedbackSettingsFromBot = (bot: FullBotUi): FeedbackSettingsUI => ({
    surveyAfterMessagesEnabled: bot.surveyAfterMessagesEnabled ?? false,
    surveyAfterMessagesCount: bot.surveyAfterMessagesCount ?? DEFAULT_SURVEY_MESSAGE_COUNT,
    surveyAfterBookingEnabled: bot.surveyAfterBookingEnabled ?? false
});

const isSameFeedbackSettings = (left: FeedbackSettingsUI, right: FeedbackSettingsUI): boolean =>
    left.surveyAfterMessagesEnabled === right.surveyAfterMessagesEnabled
    && left.surveyAfterMessagesCount === right.surveyAfterMessagesCount
    && left.surveyAfterBookingEnabled === right.surveyAfterBookingEnabled;

const localeOrder = (locale: LocaleTag): string => {
    if (locale === DEFAULT_EDITOR_LOCALE) {
        return '0';
    }
    return `1-${String(locale)}`;
};

const isLocaleTag = (value: unknown): value is LocaleTag => Object.values(LocaleTag).includes(value as LocaleTag);

const normalizeLocale = (value: unknown, fallback: LocaleTag = DEFAULT_EDITOR_LOCALE): LocaleTag =>
    isLocaleTag(value) ? value : fallback;

const normalizeConvoStarterVisibility = (value: unknown): ConvoStarterVisibility => {
    if (value === ConvoStarterVisibility.Bubble || value === 'BUBBLE') {
        return ConvoStarterVisibility.Bubble;
    }
    return ConvoStarterVisibility.Chat;
};

const normalizeClickAction = (value: unknown): ClickAction => {
    if (value === ClickAction.SendMessage || value === 'SEND_MESSAGE') {
        return ClickAction.SendMessage;
    }
    return ClickAction.RedirectToDetailsPage;
};

const normalizeConvoStarter = (item: any): UpdateWidgetConvoStartersRequest => ({
    id: typeof item?.id === 'number' ? item.id : undefined,
    message: typeof item?.message === 'string' ? item.message : '',
    visibility: normalizeConvoStarterVisibility(item?.visibility)
});

const normalizeVehicleDetailsButton = (item: any): UpdateVehicleDetailsButtonRequest => ({
    id: typeof item?.id === 'number' ? item.id : undefined,
    buttonText: typeof item?.buttonText === 'string' ? item.buttonText : 'View Vehicle',
    message: typeof item?.message === 'string' ? item.message : '',
    clickAction: normalizeClickAction(item?.clickAction)
});

const createDefaultVariant = (locale: LocaleTag): WidgetAppearanceLocaleVariantUI => ({
    ...DEFAULT_VARIANT_TEMPLATE,
    locale,
    convoStarters: [],
    vehicleDetailsButtons: []
});

const normalizeLocaleVariant = (raw: any, fallbackLocale: LocaleTag = DEFAULT_EDITOR_LOCALE): WidgetAppearanceLocaleVariantUI => {
    const locale = normalizeLocale(raw?.locale, fallbackLocale);

    return {
        id: typeof raw?.id === 'number' ? raw.id : undefined,
        locale,
        brandColour: typeof raw?.brandColour === 'string' ? raw.brandColour : DEFAULT_VARIANT_TEMPLATE.brandColour,
        widgetPosition: raw?.widgetPosition === WidgetPosition.Left || raw?.widgetPosition === 'LEFT'
            ? WidgetPosition.Left
            : WidgetPosition.Right,
        horizontalOffsetPx: typeof raw?.horizontalOffsetPx === 'number'
            ? raw.horizontalOffsetPx
            : DEFAULT_VARIANT_TEMPLATE.horizontalOffsetPx,
        verticalOffsetPx: typeof raw?.verticalOffsetPx === 'number'
            ? raw.verticalOffsetPx
            : DEFAULT_VARIANT_TEMPLATE.verticalOffsetPx,
        hidePoweredBy: typeof raw?.hidePoweredBy === 'boolean'
            ? raw.hidePoweredBy
            : DEFAULT_VARIANT_TEMPLATE.hidePoweredBy,
        showFooterMessage: typeof raw?.showFooterMessage === 'boolean'
            ? raw.showFooterMessage
            : DEFAULT_VARIANT_TEMPLATE.showFooterMessage,
        footerMessage: typeof raw?.footerMessage === 'string' ? raw.footerMessage : DEFAULT_VARIANT_TEMPLATE.footerMessage,
        showTerms: typeof raw?.showTerms === 'boolean' ? raw.showTerms : DEFAULT_VARIANT_TEMPLATE.showTerms,
        termsTitle: typeof raw?.termsTitle === 'string' ? raw.termsTitle : DEFAULT_VARIANT_TEMPLATE.termsTitle,
        termsUrl: typeof raw?.termsUrl === 'string' ? raw.termsUrl : DEFAULT_VARIANT_TEMPLATE.termsUrl,
        promptPopUpView: typeof raw?.promptPopUpView === 'boolean'
            ? raw.promptPopUpView
            : DEFAULT_VARIANT_TEMPLATE.promptPopUpView,
        popUpDelaySec: typeof raw?.popUpDelaySec === 'number' ? raw.popUpDelaySec : DEFAULT_VARIANT_TEMPLATE.popUpDelaySec,
        initialBotMessage: typeof raw?.initialBotMessage === 'string'
            ? raw.initialBotMessage
            : DEFAULT_VARIANT_TEMPLATE.initialBotMessage,
        convoStarters: Array.isArray(raw?.convoStarters)
            ? raw.convoStarters.map(normalizeConvoStarter)
            : [],
        vehicleDetailsButtons: Array.isArray(raw?.vehicleDetailsButtons)
            ? raw.vehicleDetailsButtons.map(normalizeVehicleDetailsButton)
            : [],
        enablePersonalizedVehicleDescription: typeof raw?.enablePersonalizedVehicleDescription === 'boolean'
            ? raw.enablePersonalizedVehicleDescription
            : DEFAULT_VARIANT_TEMPLATE.enablePersonalizedVehicleDescription,
        gtmAnalyticsEnabled: typeof raw?.gtmAnalyticsEnabled === 'boolean'
            ? raw.gtmAnalyticsEnabled
            : DEFAULT_VARIANT_TEMPLATE.gtmAnalyticsEnabled,
        assistantName: typeof raw?.assistantName === 'string' ? raw.assistantName : DEFAULT_VARIANT_TEMPLATE.assistantName,
        personaPrompt: typeof raw?.personaPrompt === 'string' ? raw.personaPrompt : DEFAULT_VARIANT_TEMPLATE.personaPrompt
    };
};

const cloneVariantForLocale = (source: WidgetAppearanceLocaleVariantUI, locale: LocaleTag): WidgetAppearanceLocaleVariantUI => ({
    ...source,
    id: undefined,
    locale,
    convoStarters: (source.convoStarters || []).map(starter => ({
        ...starter,
        id: undefined
    })),
    vehicleDetailsButtons: (source.vehicleDetailsButtons || []).map(button => ({
        ...button,
        id: undefined
    }))
});

const ensureBaselineVariant = (variants: WidgetAppearanceLocaleVariantUI[]): WidgetAppearanceLocaleVariantUI[] => {
    const dedupedByLocale = new Map<LocaleTag, WidgetAppearanceLocaleVariantUI>();

    variants.forEach(variant => {
        dedupedByLocale.set(variant.locale, variant);
    });

    if (!dedupedByLocale.has(DEFAULT_EDITOR_LOCALE)) {
        const baseForClone = variants[0] || createDefaultVariant(DEFAULT_EDITOR_LOCALE);
        dedupedByLocale.set(DEFAULT_EDITOR_LOCALE, cloneVariantForLocale(baseForClone, DEFAULT_EDITOR_LOCALE));
    }

    return Array.from(dedupedByLocale.values())
        .sort((left, right) => localeOrder(left.locale).localeCompare(localeOrder(right.locale)));
};

const normalizeFullWidgetAppearanceSettings = (raw: any): FullWidgetAppearanceSettingUiWithVariants => {
    const response = raw || {};
    const rawVariants = Array.isArray(response.localeVariants)
        ? response.localeVariants
        : [response];

    const normalizedVariants = ensureBaselineVariant(
        rawVariants.map((variant: any) => normalizeLocaleVariant(variant))
    );

    return {
        publicBotId: typeof response.publicBotId === 'string' ? response.publicBotId : '',
        localeVariants: normalizedVariants
    };
};

const normalizeDefaultWidgetAppearanceSettings = (raw: any): WidgetAppearanceLocaleVariantUI => {
    const normalized = normalizeLocaleVariant(raw, DEFAULT_EDITOR_LOCALE);
    normalized.locale = DEFAULT_EDITOR_LOCALE;
    normalized.id = undefined;

    normalized.convoStarters = (normalized.convoStarters || []).map(starter => ({
        ...starter,
        id: undefined
    }));

    normalized.vehicleDetailsButtons = (normalized.vehicleDetailsButtons || []).map(button => ({
        ...button,
        id: undefined
    }));

    return normalized;
};

const getConfiguredLocalesFromVariants = (variants: WidgetAppearanceLocaleVariantUI[]): LocaleTag[] =>
    ensureBaselineVariant(variants).map(variant => variant.locale);

const getVariantByLocale = (variants: WidgetAppearanceLocaleVariantUI[], locale: LocaleTag): WidgetAppearanceLocaleVariantUI | undefined =>
    variants.find(variant => variant.locale === locale);

const getSelectedVariant = (state: WidgetAppearanceState): WidgetAppearanceLocaleVariantUI => {
    const existing = getVariantByLocale(state.updateRequest.localeVariants, state.selectedLocale);
    if (existing) {
        return existing;
    }

    const fallback = getVariantByLocale(state.updateRequest.localeVariants, state.defaultLocale)
        || state.updateRequest.localeVariants[0]
        || createDefaultVariant(state.defaultLocale);

    if (!state.updateRequest.localeVariants.includes(fallback)) {
        state.updateRequest.localeVariants.push(fallback);
    }

    state.selectedLocale = fallback.locale;
    return fallback;
};

const buildUpdatePayload = (request: UpdateWidgetAppearanceSettingsRequestUI): UpdateWidgetAppearanceSettingsRequestUI => ({
    publicBotId: request.publicBotId,
    localeVariants: ensureBaselineVariant(request.localeVariants).map(variant => ({
        ...variant,
        convoStarters: (variant.convoStarters || []).map(starter => ({
            id: starter.id,
            message: starter.message,
            visibility: starter.visibility
        })),
        vehicleDetailsButtons: (variant.vehicleDetailsButtons || []).map(button => ({
            id: button.id,
            buttonText: button.buttonText,
            message: button.message,
            clickAction: button.clickAction
        }))
    }))
});

export interface WidgetAppearanceLocaleVariantUI {
    id?: number;
    locale: LocaleTag;
    brandColour: string;
    widgetPosition: WidgetPosition;
    horizontalOffsetPx: number;
    verticalOffsetPx: number;
    hidePoweredBy: boolean;
    showFooterMessage: boolean;
    footerMessage: string;
    showTerms: boolean;
    termsTitle: string;
    termsUrl: string;
    promptPopUpView: boolean;
    popUpDelaySec: number;
    initialBotMessage: string;
    convoStarters: UpdateWidgetConvoStartersRequest[];
    vehicleDetailsButtons: UpdateVehicleDetailsButtonRequest[];
    enablePersonalizedVehicleDescription: boolean;
    gtmAnalyticsEnabled: boolean;
    assistantName: string;
    personaPrompt: string;
}

export interface FullWidgetAppearanceSettingUiWithVariants {
    publicBotId: string;
    localeVariants: WidgetAppearanceLocaleVariantUI[];
}

export interface UpdateWidgetAppearanceSettingsRequestUI {
    publicBotId: string;
    localeVariants: WidgetAppearanceLocaleVariantUI[];
}

export type UpdateWidgetAppearanceVariantPartial = Partial<WidgetAppearanceLocaleVariantUI>;

export const fetchWidgetAppearanceSettingById = createAsyncThunk<FullWidgetAppearanceSettingUiWithVariants, string>(
    'widgetAppearance/fetchById',
    async (publicBotId: string) => {
        const response = await widgetAppearanceApi.getWidgetAppearanceSettingsByPublicBotId({publicBotId});
        return normalizeFullWidgetAppearanceSettings(response as any);
    }
);

export const fetchDefaultWidgetAppearanceSettings = createAsyncThunk<WidgetAppearanceLocaleVariantUI, BotType>(
    'widgetAppearance/fetchDefault',
    async (botType: BotType) => {
        const response = await widgetAppearanceApi.getDefaultWidgetAppearanceSettings({botType});
        return normalizeDefaultWidgetAppearanceSettings(response as any);
    }
);

export const updateWidgetAppearanceSettings = createAsyncThunk<FullWidgetAppearanceSettingUiWithVariants, string, {
    state: RootState,
    rejectValue: string | null
}>(
    'widgetAppearance/update',
    async (publicBotId, {getState, rejectWithValue}) => {
        const state = getState();
        const payload = buildUpdatePayload({
            ...state.widgetAppearance.updateRequest,
            publicBotId
        });

        try {
            const response = await widgetAppearanceApi.updateWidgetAppearanceSettings({
                updateWidgetAppearanceSettingsRequest: payload as any
            });
            return normalizeFullWidgetAppearanceSettings(response as any);
        } catch (error: any) {
            const message = error?.response ? await error.response.text() : null;
            return rejectWithValue(message);
        }
    }
);

export const updateFeedbackSettingsOfBot = createAsyncThunk<FeedbackSettingsUI, string, {
    state: RootState,
    rejectValue: string | null
}>(
    'widgetAppearance/updateFeedbackSettings',
    async (publicBotId, {getState, rejectWithValue}) => {
        const feedback = getState().widgetAppearance.feedback;
        try {
            return await botApi.updateBotFeedbackSettings({
                updateBotFeedbackSettingsRequest: {
                    publicBotId,
                    surveyAfterMessagesEnabled: feedback.surveyAfterMessagesEnabled,
                    surveyAfterMessagesCount: normalizeSurveyMessageCount(feedback.surveyAfterMessagesCount),
                    surveyAfterBookingEnabled: feedback.surveyAfterBookingEnabled
                }
            });
        } catch (error: any) {
            const message = error?.response ? await error.response.text() : null;
            return rejectWithValue(message);
        }
    }
);

export interface WidgetAppearanceState {
    widgetAppearanceSetting?: FullWidgetAppearanceSettingUiWithVariants;
    updateRequest: UpdateWidgetAppearanceSettingsRequestUI;
    widgetAppearanceSettingRequestStatus: RequestStatus;
    updateSettingsStatus: RequestStatus;
    defaultSettingsLoadingStatus: RequestStatus;
    hasUnsavedChanges: boolean;
    feedback: FeedbackSettingsUI;
    savedFeedback: FeedbackSettingsUI;
    updateFeedbackStatus: RequestStatus;
    defaultLocale: LocaleTag;
    assistantDefaultLocale: LocaleTag;
    selectedLocale: LocaleTag;
    configuredLocales: LocaleTag[];
}

const initialState: WidgetAppearanceState = {
    widgetAppearanceSetting: undefined,
    updateRequest: {
        publicBotId: '',
        localeVariants: [createDefaultVariant(DEFAULT_EDITOR_LOCALE)]
    },
    widgetAppearanceSettingRequestStatus: RequestStatus.NONE,
    updateSettingsStatus: RequestStatus.NONE,
    defaultSettingsLoadingStatus: RequestStatus.NONE,
    hasUnsavedChanges: false,
    feedback: DEFAULT_FEEDBACK_SETTINGS,
    savedFeedback: DEFAULT_FEEDBACK_SETTINGS,
    updateFeedbackStatus: RequestStatus.NONE,
    defaultLocale: DEFAULT_EDITOR_LOCALE,
    assistantDefaultLocale: DEFAULT_EDITOR_LOCALE,
    selectedLocale: DEFAULT_EDITOR_LOCALE,
    configuredLocales: [DEFAULT_EDITOR_LOCALE]
};

export const widgetAppearanceSlice = createSlice({
    name: 'widgetAppearance',
    initialState,
    reducers: {
        updateSettings: (state, action: PayloadAction<UpdateWidgetAppearanceVariantPartial>) => {
            const variant = getSelectedVariant(state);
            Object.assign(variant, action.payload);
            state.hasUnsavedChanges = true;
        },
        setLocaleContext: (state, action: PayloadAction<LocaleTag>) => {
            state.defaultLocale = DEFAULT_EDITOR_LOCALE;
            state.assistantDefaultLocale = normalizeLocale(action.payload, DEFAULT_EDITOR_LOCALE);
            state.updateRequest.localeVariants = ensureBaselineVariant(state.updateRequest.localeVariants);
            state.configuredLocales = getConfiguredLocalesFromVariants(state.updateRequest.localeVariants);

            if (state.configuredLocales.includes(state.assistantDefaultLocale)) {
                state.selectedLocale = state.assistantDefaultLocale;
            } else if (!state.configuredLocales.includes(state.selectedLocale)) {
                state.selectedLocale = state.defaultLocale;
            }
        },
        setSelectedLocale: (state, action: PayloadAction<LocaleTag>) => {
            if (state.configuredLocales.includes(action.payload)) {
                state.selectedLocale = action.payload;
            }
        },
        addConfiguredLocale: (state, action: PayloadAction<LocaleTag>) => {
            const localeToAdd = action.payload;
            if (state.configuredLocales.includes(localeToAdd)) {
                state.selectedLocale = localeToAdd;
                return;
            }

            const baselineVariant = getVariantByLocale(state.updateRequest.localeVariants, state.defaultLocale)
                || state.updateRequest.localeVariants[0]
                || createDefaultVariant(state.defaultLocale);

            const clone = cloneVariantForLocale(baselineVariant, localeToAdd);
            state.updateRequest.localeVariants.push(clone);
            state.updateRequest.localeVariants = ensureBaselineVariant(state.updateRequest.localeVariants);
            state.configuredLocales = getConfiguredLocalesFromVariants(state.updateRequest.localeVariants);
            state.selectedLocale = localeToAdd;
            state.hasUnsavedChanges = true;
        },
        removeConfiguredLocale: (state, action: PayloadAction<LocaleTag>) => {
            const localeToRemove = action.payload;
            if (localeToRemove === state.defaultLocale) {
                return;
            }

            state.updateRequest.localeVariants = state.updateRequest.localeVariants.filter(
                variant => variant.locale !== localeToRemove
            );
            state.updateRequest.localeVariants = ensureBaselineVariant(state.updateRequest.localeVariants);
            state.configuredLocales = getConfiguredLocalesFromVariants(state.updateRequest.localeVariants);

            if (!state.configuredLocales.includes(state.selectedLocale)) {
                state.selectedLocale = state.defaultLocale;
            }

            state.hasUnsavedChanges = true;
        },
        updateLocalizedField: (state, action: PayloadAction<{field: 'initialBotMessage' | 'footerMessage' | 'termsTitle' | 'termsUrl'; value: string}>) => {
            const variant = getSelectedVariant(state);
            variant[action.payload.field] = action.payload.value;
            state.hasUnsavedChanges = true;
        },
        setFeedbackSettings: (state, action: PayloadAction<FeedbackSettingsUI>) => {
            state.feedback = action.payload;
            state.savedFeedback = action.payload;
        },
        updateFeedbackSettings: (state, action: PayloadAction<FeedbackSettingsPartial>) => {
            Object.assign(state.feedback, action.payload);
        }
    },
    extraReducers: (builder) => {
        builder.addCase(fetchWidgetAppearanceSettingById.fulfilled, (state, {payload}) => {
            state.widgetAppearanceSetting = payload;
            state.widgetAppearanceSettingRequestStatus = RequestStatus.RESOLVED;

            state.updateRequest = {
                publicBotId: payload.publicBotId,
                localeVariants: ensureBaselineVariant(payload.localeVariants)
            };

            state.configuredLocales = getConfiguredLocalesFromVariants(state.updateRequest.localeVariants);

            if (state.configuredLocales.includes(state.assistantDefaultLocale)) {
                state.selectedLocale = state.assistantDefaultLocale;
            } else if (!state.configuredLocales.includes(state.selectedLocale)) {
                state.selectedLocale = state.defaultLocale;
            }

            state.hasUnsavedChanges = false;
        });

        builder.addCase(fetchWidgetAppearanceSettingById.pending, (state) => {
            state.widgetAppearanceSettingRequestStatus = RequestStatus.PENDING;
        });

        builder.addCase(fetchWidgetAppearanceSettingById.rejected, (state) => {
            state.widgetAppearanceSettingRequestStatus = RequestStatus.REJECTED;
        });

        builder.addCase(fetchDefaultWidgetAppearanceSettings.pending, (state) => {
            state.defaultSettingsLoadingStatus = RequestStatus.PENDING;
        });

        builder.addCase(fetchDefaultWidgetAppearanceSettings.fulfilled, (state, {payload}) => {
            state.defaultSettingsLoadingStatus = RequestStatus.RESOLVED;

            state.updateRequest = {
                publicBotId: state.updateRequest.publicBotId,
                localeVariants: [payload]
            };

            state.configuredLocales = [state.defaultLocale];
            state.selectedLocale = state.defaultLocale;
            state.feedback = {...DEFAULT_FEEDBACK_SETTINGS};
            state.hasUnsavedChanges = true;
        });

        builder.addCase(fetchDefaultWidgetAppearanceSettings.rejected, (state) => {
            state.defaultSettingsLoadingStatus = RequestStatus.REJECTED;
        });

        builder.addCase(updateWidgetAppearanceSettings.pending, (state) => {
            state.updateSettingsStatus = RequestStatus.PENDING;
        });

        builder.addCase(updateWidgetAppearanceSettings.fulfilled, (state, {payload}) => {
            state.updateSettingsStatus = RequestStatus.RESOLVED;
            state.widgetAppearanceSetting = payload;

            state.updateRequest = {
                publicBotId: payload.publicBotId,
                localeVariants: ensureBaselineVariant(payload.localeVariants)
            };

            state.configuredLocales = getConfiguredLocalesFromVariants(state.updateRequest.localeVariants);

            if (!state.configuredLocales.includes(state.selectedLocale)) {
                state.selectedLocale = state.defaultLocale;
            }

            state.hasUnsavedChanges = false;
        });

        builder.addCase(updateWidgetAppearanceSettings.rejected, (state) => {
            state.updateSettingsStatus = RequestStatus.REJECTED;
        });

        builder.addCase(updateFeedbackSettingsOfBot.pending, (state) => {
            state.updateFeedbackStatus = RequestStatus.PENDING;
        });

        builder.addCase(updateFeedbackSettingsOfBot.fulfilled, (state, {payload}) => {
            state.updateFeedbackStatus = RequestStatus.RESOLVED;
            state.feedback = payload;
            state.savedFeedback = payload;
        });

        builder.addCase(updateFeedbackSettingsOfBot.rejected, (state) => {
            state.updateFeedbackStatus = RequestStatus.REJECTED;
        });
    }
});

export const {
    updateSettings,
    setLocaleContext,
    setSelectedLocale,
    addConfiguredLocale,
    removeConfiguredLocale,
    updateLocalizedField,
    setFeedbackSettings,
    updateFeedbackSettings
} = widgetAppearanceSlice.actions;

const getCurrentVariantFromState = (state: RootState): WidgetAppearanceLocaleVariantUI => {
    const variants = state.widgetAppearance.updateRequest.localeVariants;
    const selectedLocale = state.widgetAppearance.selectedLocale;
    const defaultLocale = state.widgetAppearance.defaultLocale;

    return variants.find(variant => variant.locale === selectedLocale)
        || variants.find(variant => variant.locale === defaultLocale)
        || variants[0]
        || createDefaultVariant(defaultLocale);
};

export const widgetAppearanceSettingsSelector = (state: RootState): FullWidgetAppearanceSettingUiWithVariants | undefined =>
    state.widgetAppearance.widgetAppearanceSetting;

export const widgetAppearanceSettingRequestStatusSelector = (state: RootState): RequestStatus =>
    state.widgetAppearance.widgetAppearanceSettingRequestStatus;

export const selectWidgetAppearanceRequest = (state: RootState): UpdateWidgetAppearanceSettingsRequestUI =>
    state.widgetAppearance.updateRequest;

export const selectSelectedLocaleVariant = (state: RootState): WidgetAppearanceLocaleVariantUI =>
    getCurrentVariantFromState(state);

export const selectBrandColour = (state: RootState): string => getCurrentVariantFromState(state).brandColour;
export const selectWidgetPosition = (state: RootState): WidgetPosition => getCurrentVariantFromState(state).widgetPosition;
export const selectHorizontalOffset = (state: RootState): number => getCurrentVariantFromState(state).horizontalOffsetPx;
export const selectVerticalOffset = (state: RootState): number => getCurrentVariantFromState(state).verticalOffsetPx;
export const selectHidePoweredBy = (state: RootState): boolean => getCurrentVariantFromState(state).hidePoweredBy;
export const selectShowFooterMessage = (state: RootState): boolean => getCurrentVariantFromState(state).showFooterMessage;
export const selectPromptPopUpView = (state: RootState): boolean => getCurrentVariantFromState(state).promptPopUpView;
export const selectPopUpDelaySec = (state: RootState): number => getCurrentVariantFromState(state).popUpDelaySec;

export const selectUpdateStatus = (state: RootState): RequestStatus => state.widgetAppearance.updateSettingsStatus;
export const selectDefaultSettingsLoadingStatus = (state: RootState): RequestStatus => state.widgetAppearance.defaultSettingsLoadingStatus;
export const selectUpdateFeedbackStatus = (state: RootState): RequestStatus => state.widgetAppearance.updateFeedbackStatus;
export const selectHasUnsavedFeedbackChanges = (state: RootState): boolean =>
    !isSameFeedbackSettings(state.widgetAppearance.feedback, state.widgetAppearance.savedFeedback);
export const selectHasUnsavedChanges = (state: RootState): boolean =>
    state.widgetAppearance.hasUnsavedChanges || selectHasUnsavedFeedbackChanges(state);

export const selectSurveyAfterMessagesEnabled = (state: RootState): boolean =>
    state.widgetAppearance.feedback.surveyAfterMessagesEnabled;
export const selectSurveyAfterMessagesCount = (state: RootState): number | undefined =>
    state.widgetAppearance.feedback.surveyAfterMessagesCount;
export const selectSurveyAfterBookingEnabled = (state: RootState): boolean =>
    state.widgetAppearance.feedback.surveyAfterBookingEnabled;

export const selectEnablePersonalizedVehicleDescription = (state: RootState): boolean =>
    getCurrentVariantFromState(state).enablePersonalizedVehicleDescription;

export const selectGtmAnalyticsEnabled = (state: RootState): boolean =>
    getCurrentVariantFromState(state).gtmAnalyticsEnabled;

export const selectDefaultLocale = (state: RootState): LocaleTag => state.widgetAppearance.defaultLocale;
export const selectSelectedLocale = (state: RootState): LocaleTag => state.widgetAppearance.selectedLocale;
export const selectConfiguredLocales = (state: RootState): LocaleTag[] => state.widgetAppearance.configuredLocales;
export const selectIsDefaultLocaleSelected = (state: RootState): boolean =>
    state.widgetAppearance.selectedLocale === state.widgetAppearance.defaultLocale;

export const selectInitialBotMessage = (state: RootState): string => getCurrentVariantFromState(state).initialBotMessage;
export const selectFooterMessage = (state: RootState): string => getCurrentVariantFromState(state).footerMessage;
export const selectShowTerms = (state: RootState): boolean => getCurrentVariantFromState(state).showTerms;
export const selectTermsTitle = (state: RootState): string => getCurrentVariantFromState(state).termsTitle;
export const selectTermsUrl = (state: RootState): string => getCurrentVariantFromState(state).termsUrl;
export const selectAssistantName = (state: RootState): string => getCurrentVariantFromState(state).assistantName;
export const selectPersonaPrompt = (state: RootState): string => getCurrentVariantFromState(state).personaPrompt;

export const selectConvoStarters = (state: RootState): UpdateWidgetConvoStartersRequest[] =>
    getCurrentVariantFromState(state).convoStarters || [];

export const selectBubbleConvoStarters = (state: RootState): string[] =>
    (getCurrentVariantFromState(state).convoStarters || [])
        .filter(starter => starter.visibility === ConvoStarterVisibility.Bubble)
        .map(starter => starter.message);

export const selectChatConvoStarters = (state: RootState): string[] =>
    (getCurrentVariantFromState(state).convoStarters || [])
        .filter(starter => starter.visibility === ConvoStarterVisibility.Chat)
        .map(starter => starter.message);

export const selectVehicleDetailsButtons = (state: RootState): UpdateVehicleDetailsButtonRequest[] =>
    getCurrentVariantFromState(state).vehicleDetailsButtons || [];

export const makeSelectVehicleButtonText = (index: number) => (state: RootState) =>
    selectVehicleDetailsButtons(state)?.[index]?.buttonText;

export const makeSelectVehicleButtonMessage = (index: number) => (state: RootState) =>
    selectVehicleDetailsButtons(state)?.[index]?.message;

export const addLocaleWithDefaults = createAsyncThunk<void, {botType: BotType; locale: LocaleTag}, {state: RootState}>(
    'widgetAppearance/addLocaleWithDefaults',
    async ({botType, locale}, {dispatch, getState}) => {
        if (getState().widgetAppearance.configuredLocales.includes(locale)) {
            dispatch(addConfiguredLocale(locale));
            return;
        }

        let openingMessage: string | undefined;
        let termsTitle: string | undefined;
        try {
            const defaults = await widgetAppearanceApi.getDefaultWidgetAppearanceSettings({botType, locale});
            openingMessage = defaults.initialBotMessage;
            termsTitle = defaults.termsTitle;
        } catch {
            openingMessage = undefined;
            termsTitle = undefined;
        }

        dispatch(addConfiguredLocale(locale));
        if (openingMessage) {
            dispatch(updateLocalizedField({field: 'initialBotMessage', value: openingMessage}));
        }
        if (termsTitle) {
            dispatch(updateLocalizedField({field: 'termsTitle', value: termsTitle}));
        }
    }
);

export const updateBubbleConvoStarters =(texts: string[]) => (dispatch: any, getState: () => RootState) => {
    updateConvoStartersByVisibility(texts, ConvoStarterVisibility.Bubble)(dispatch, getState);
};

export const updateChatConvoStarters = (texts: string[]) => (dispatch: any, getState: () => RootState) => {
    updateConvoStartersByVisibility(texts, ConvoStarterVisibility.Chat)(dispatch, getState);
};

const updateConvoStartersByVisibility = (texts: string[], visibility: ConvoStarterVisibility) => (dispatch: any, getState: () => RootState) => {
    const state = getState();
    const currentVariant = getCurrentVariantFromState(state);
    const allStarters = currentVariant.convoStarters || [];

    const nonThisVisibilityStarters = allStarters.filter(starter => starter.visibility !== visibility);
    const existingThisVisibilityStarters = allStarters.filter(starter => starter.visibility === visibility);

    const thisVisibilityStarters = texts.map((text, index) => {
        const existing = existingThisVisibilityStarters[index];
        return {
            id: existing?.id,
            message: text,
            visibility
        };
    });

    dispatch(updateSettings({
        convoStarters: [...nonThisVisibilityStarters, ...thisVisibilityStarters]
    }));
};

export const updateVehicleDetailsButtons = (buttons: UpdateVehicleDetailsButtonRequest[]) => (dispatch: any) => {
    dispatch(updateSettings({
        vehicleDetailsButtons: buttons
    }));
};

export default widgetAppearanceSlice.reducer;
